import { LucideX } from 'lucide-react'
import { useEffect, useRef } from 'react'
import type { PopupProps } from 'react-map-gl'
import { Popup } from 'react-map-gl'
import { Button } from '~/components/ui'
import { useBreakpoint } from '~/hooks/browser'
import { cn } from '~/lib'

type MobilePopupProps = PopupProps &
  React.RefAttributes<mapboxgl.Popup> & {
    breakpoint?: number
    children: React.ReactNode
    className?: string
    onClose?: () => void
    ariaLabel?: string
  }

export default function MobilePopup({
  breakpoint = 1024,
  children,
  className,
  onClose,
  ariaLabel = 'Details',
  ...props
}: MobilePopupProps) {
  const { mdDown } = useBreakpoint()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const closeButtonRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (!mdDown) return
    const container = containerRef.current
    if (!container) return
    const focusable = getFocusableElements(container)
    const target = closeButtonRef.current || focusable[0] || container
    target.focus()
  }, [mdDown])

  const onDialogKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation()
      onClose?.()
      return
    }
    if (e.key !== 'Tab') return
    const container = containerRef.current
    if (!container) return
    const focusable = getFocusableElements(container)
    if (focusable.length === 0) {
      e.preventDefault()
      container.focus()
      return
    }
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const active = document.activeElement as HTMLElement | null
    if (e.shiftKey) {
      if (!active || active === first) {
        e.preventDefault()
        last.focus()
      }
    } else if (active === last) {
      e.preventDefault()
      first.focus()
    }
  }

  if (mdDown) {
    return (
      <div
        className="fixed bottom-0 left-0 right-0 top-0 z-50"
        onClick={onClose}>
        <div
          ref={containerRef}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 flex w-[95dvw] max-w-[95dvw] -translate-x-1/2 -translate-y-1/2 flex-col rounded-md bg-background',
            className
          )}
          role="dialog"
          aria-modal="true"
          aria-label={ariaLabel}
          tabIndex={-1}
          onKeyDown={onDialogKeyDown}
          onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="default"
            className="absolute right-0 top-0 h-10 bg-background px-3"
            ref={closeButtonRef}
            aria-label="Close popup"
            onClick={onClose}>
            <LucideX className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </Button>
          <div className="-mt-1.5 p-4">{children}</div>
        </div>
      </div>
    )
  }

  return (
    <Popup className={className} {...props} onClose={onClose}>
      {children}
    </Popup>
  )
}

function getFocusableElements(container: HTMLElement) {
  const selectors = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ]
  return Array.from(
    container.querySelectorAll<HTMLElement>(selectors.join(','))
  ).filter(
    (el) => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden')
  )
}
